import { memo } from 'react';
import {
  DataTableDataSearchDto,
  DatatableDataUserGroupSelectionDto,
  FooterDataDto,
  ManualDefinitionTableDataObject,
  PageDto,
  TableColumnConfigDto,
  TableDataDto,
  TableRowDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  ButtonsCellRenderer,
  CheckboxCellRendererDecorator,
  combineFormatters,
  DetailPageCellRenderer,
  FileDownloadCellRenderer,
  FileNameCellRenderer,
  IconCellRenderer,
  IIconCellRendererParams,
  LoadingIndicator,
  MultiValueCellRenderer,
  numberFactorFormatter,
  ProgressBarCellRenderer,
  ProgressBarProps,
  triggerDownloadWithAnchorTag,
  WorkflowStepFile,
  WorkflowStepModalComment,
  WorkflowVisualizationCellRenderer,
} from '@asset-metrix/portal-ui';
import {
  type CellRendererSelectorFunc,
  CellRendererSelectorResult,
  GROUP_AUTO_COLUMN_ID,
  type ICellRendererParams,
} from 'ag-grid-community';

import { portalGatewayClient } from '../../api/portalGatewayClient';
import { SigningRequest } from '../../store/states/documentSigning';
import { invalidateTable } from '../../utils/queryCacheHelper';

import { formattersByType } from './configToColDef';
import { DetailPageCellRendererContainer } from './DetailPageCellRendererContainer';
import { extractValue } from './extractValue';
import {
  FooterCellRenderer,
  FooterCellRendererProps,
} from './FooterCellRenderer';
import {
  getDetailPaneCellRendererDecorator,
  isTableColumnConfigWithDetailPane,
} from './getDetailPaneCellRendererDecorator';
import { isPageFilterColumnConfig } from './usePageFilterColumnState';
import { getRowId } from './widgetBulkAction';

const TEXT_LINK_WORKFLOW_ACTIONS = ['cancel', 'reject', 'close'];
// Not a typo - should match validate and validation
const TEXT_LINK_WORKFLOW_ACTIONS_EXCLUSION = ['validat'];

function isTextLinkWorkflowAction(label: unknown): boolean {
  const normalizedLabel = String(label).toLowerCase();

  return (
    TEXT_LINK_WORKFLOW_ACTIONS.some((action) =>
      normalizedLabel.includes(action)
    ) &&
    !TEXT_LINK_WORKFLOW_ACTIONS_EXCLUSION.some((exclusion) =>
      normalizedLabel.includes(exclusion)
    )
  );
}

export type CellRendererSelectorOptions = {
  dataConfigs?: TableDataDto['configs'];
  pageData?: PageDto;
  isFullScreenMode?: boolean;
  isGroupOnlyMode?: boolean;
  isGroupedTable?: boolean;
  isSomeDataRowVisible?: boolean;
  pageFilter?: DataTableDataSearchDto['selection'];
  showFullTextSearchResults?: boolean;
  workflowActionHandler?: (
    modalId: string,
    rowIds: string[],
    action: string,
    data: TableRowDto['data'][],
    documentId?: string
  ) => void;
  readonlyWorkflowActionHandler?: (
    modalId: string,
    data: TableRowDto['data'][]
  ) => void;
  footerData?: FooterDataDto;
  footerTitleColumnKey?: string;
  currentSigningRequests?: SigningRequest[];
  isSigningInProgress?: boolean;
  setFilterFromPageFilterColumn?: (
    event: React.ChangeEvent<HTMLInputElement>,
    params: ICellRendererParams<TableRowDto['data']>
  ) => void;
  isPageFilterColumnChecked?: (
    params: ICellRendererParams<TableRowDto['data']>
  ) => boolean;
  saveInlineCell?: (
    params: ICellRendererParams<TableRowDto['data']>,
    columnKey: string,
    nextValue: boolean
  ) => void;
  isInlineCellPending?: (rowId: string, columnKey: string) => boolean;
  rowIdToHighlight?: string;
  userGroupSelection?: DatatableDataUserGroupSelectionDto['userGroupSelection'];
  investmentLimitLinkColumnKey?: string | null;
  onInvestmentLimitRowOpened?: (row: TableRowDto['data']) => void;
};

export function getDefaultCellRenderer(
  config: TableColumnConfigDto,
  params: ICellRendererParams<TableRowDto['data']>,
  widgetId: WidgetDto['id'],
  {
    currentSigningRequests,
    dataConfigs,
    investmentLimitLinkColumnKey,
    isGroupOnlyMode,
    isSigningInProgress,
    onInvestmentLimitRowOpened,
    pageFilter,
    pageData,
    readonlyWorkflowActionHandler,
    showFullTextSearchResults,
    workflowActionHandler,
  }: CellRendererSelectorOptions = {}
): CellRendererSelectorResult | undefined {
  if (config.type === 'file_name') {
    return {
      component: memo(FileNameCellRenderer),
      params: {
        onFileDownload: async (
          fileId: string,
          shouldRefetchTableData = false
        ) => {
          try {
            const { data } =
              await portalGatewayClient.table.getDocumentDownloadUrl(
                fileId,
                widgetId
              );
            if (data.status === 'success' && data.data.url) {
              triggerDownloadWithAnchorTag(data.data.url);
              shouldRefetchTableData &&
                (await invalidateTable({ dataSourceType: 'docview' }));
            }
          } catch (e: unknown) {
            console.error(e);
            throw e;
          }
        },
        showFullTextSearchResults,
        showUnreadIndicator: config.showUnreadIndicator,
      },
    };
  } else if (config.type === 'fileDownload') {
    return {
      component: memo(FileDownloadCellRenderer),
      params: {
        onFileClick: async (file: WorkflowStepFile) => {
          try {
            const { data } =
              await portalGatewayClient.table.getDocumentDownloadUrl(
                file.id,
                widgetId,
                {
                  token: file.downloadToken,
                  parentDocumentId: file.parentId,
                }
              );
            if (data.status === 'success' && data.data.url) {
              triggerDownloadWithAnchorTag(data.data.url);
            }
          } catch (e: unknown) {
            console.error(e);
            throw e;
          }
        },
      },
    };
  } else if (
    config.type === 'text' &&
    investmentLimitLinkColumnKey != null &&
    config.key === investmentLimitLinkColumnKey
  ) {
    // Grouped rows do not carry the stored limit payload this summary reads.
    if (params.node.group || isGroupOnlyMode) {
      return undefined;
    }

    return {
      component: memo(DetailPageCellRenderer),
      params: {
        onClick: () => {
          if (params.data != null) {
            onInvestmentLimitRowOpened?.(params.data);
          }
        },
      },
    };
  } else if (config.type === 'link') {
    // We do not support detail page links on grouped rows or group-only visualization rows,
    // because they might not contain all the necessary data to be passed to the detail page.
    if (params.node.group || isGroupOnlyMode) {
      return undefined;
    }

    return {
      component: memo(DetailPageCellRendererContainer),
      params: {
        detailPageConfig: getDetailPageConfig(config, params, dataConfigs),
        pageFilter,
        pageData,
      },
    };
  } else if (config.type === 'workflowActions') {
    // workflows active
    if (params.value?.workflowStatus?.state === 'active') {
      if (params.value.isInTransientState) {
        return {
          component: memo(LoadingIndicator),
        };
      }

      return {
        component: memo(ButtonsCellRenderer),
        params: {
          buttons: Array.isArray(params.value.actions)
            ? params.value.actions
                .filter((action: ManualDefinitionTableDataObject) => {
                  // Don't show non-active text link wf actions or non-active button wf actions without a hint
                  return !(
                    action.disabled &&
                    (isTextLinkWorkflowAction(action.label) || !action.hint)
                  );
                })
                .map((action: ManualDefinitionTableDataObject) => ({
                  children: action.label,
                  loading: currentSigningRequests?.some(
                    (r) => r.documentId === action.documentId
                  ),
                  disabled: isSigningInProgress || action.disabled,
                  tooltip: action.hint,
                  onClick: () => {
                    if (
                      workflowActionHandler != null &&
                      action.key != null &&
                      typeof action.key === 'string' &&
                      typeof action.modalId === 'string' &&
                      params.data !== undefined
                    ) {
                      workflowActionHandler(
                        action.modalId,
                        [String(params.value.rowId)],
                        action.key,
                        [params.data],
                        typeof action.documentId === 'string'
                          ? action.documentId
                          : undefined
                      );
                    }
                  },
                  variant: isTextLinkWorkflowAction(action.label)
                    ? 'text'
                    : 'primary',
                }))
            : [],
        },
      };
    }

    // workflows locked
    return {
      component: memo(IconCellRenderer),
      params: {
        value: {
          name: 'lock',
          disabled: true,
          tooltip: params.value?.workflowStatus?.stateHint,
        },
      } as IIconCellRendererParams,
    };
  } else if (config.type === 'workflowProgress') {
    const progressBar: ProgressBarProps = {
      segments: Array.isArray(params.value)
        ? params.value.map((v, idx) => {
            return {
              ...v,
              value: numberFactorFormatter(v.value, config) ?? v.value,
              valueFormatted: combineFormatters(
                formattersByType['number'],
                config
              )(v),
            };
          })
        : [],
      unit: '%',
    };

    return {
      component: memo(ProgressBarCellRenderer),
      params: {
        progressBar,
      },
    };
  } else if (config.type === 'workflowVisualization') {
    return {
      component: memo(WorkflowVisualizationCellRenderer),
      params: {
        onFileClick: async (file: WorkflowStepFile) => {
          try {
            const { data } =
              await portalGatewayClient.table.getDocumentDownloadUrl(
                file.id,
                widgetId,
                {
                  token: file.downloadToken,
                  parentDocumentId: file.parentId,
                }
              );
            if (data.status === 'success' && data.data.url) {
              triggerDownloadWithAnchorTag(data.data.url);
            }
          } catch (e: unknown) {
            console.error(e);
            throw e;
          }
        },
        onCommentClick: (comment: WorkflowStepModalComment) => {
          if (
            readonlyWorkflowActionHandler != null &&
            params.data !== undefined
          ) {
            readonlyWorkflowActionHandler(comment.modalId, [params.data]);
          }
        },
      },
    };
  } else if (params.value == null) {
    // Maybe ag-grid failed to extract the correct value
    const extractedValue = extractValue(config.key, params.data);

    if (Array.isArray(extractedValue)) {
      return {
        component: memo(MultiValueCellRenderer),
        params: {
          value: extractedValue,
        },
      };
    }
  }
}

function getDetailPageConfig(
  config: TableColumnConfigDto,
  params: ICellRendererParams,
  dataConfigs: CellRendererSelectorOptions['dataConfigs']
) {
  const detailPages =
    (params.data?.originalRowIndex != null &&
      dataConfigs?.[params.data.originalRowIndex]?.detailPages) ||
    [];
  const detailPage = detailPages.find(
    (dp) => dp.datacolumnKey === params.column?.getColId()
  );
  return config.detailPageConfig?.find(
    (dpc) => dpc.id === detailPage?.detailPageId
  );
}

function getFooterParams(
  columnConfig: TableColumnConfigDto,
  params: ICellRendererParams,
  isFooterLoading: boolean,
  footerTitleColumnKey?: string
): FooterCellRendererProps {
  if (footerTitleColumnKey === params.column?.getColId()) {
    return {
      isTitle: true,
      titleValue: params.value,
    };
  }

  if (!columnConfig.appearsInFooter) {
    return {
      isTitle: false,
      isEnabled: false,
    };
  }

  return {
    isEnabled: true,
    isTitle: false,
    isLoading: isFooterLoading,
    valueFormatted: params.valueFormatted ?? '',
  };
}

function getCheckboxCellRendererDecoratorParams(
  params: ICellRendererParams,
  cellRendererConfig: CellRendererSelectorResult | undefined,
  tableColumnConfigDto: TableColumnConfigDto,
  options?: CellRendererSelectorOptions
) {
  return {
    cellRendererConfig,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      options?.setFilterFromPageFilterColumn?.(event, params);
    },
    isChecked: options?.isPageFilterColumnChecked?.(params) ?? false,
    align: tableColumnConfigDto.align,
  };
}

function getInlineEditableCellRendererParams(
  params: ICellRendererParams<TableRowDto['data']>,
  tableColumnConfigDto: TableColumnConfigDto,
  options?: CellRendererSelectorOptions
) {
  const rowData = params.data;
  const rowId = rowData != null ? getRowId(rowData) : undefined;
  const isPending =
    rowId != null &&
    (options?.isInlineCellPending?.(rowId, tableColumnConfigDto.key) ?? false);

  return {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      options?.saveInlineCell?.(
        params,
        tableColumnConfigDto.key,
        event.target.checked
      );
    },
    // Null and any non-boolean stay unchecked. The first click saves true.
    isChecked: params.value === true,
    align: tableColumnConfigDto.align,
    disabled: rowId == null || isPending,
    // This decorator also draws the cell text beside the checkbox (page filters).
    // Here the cell value is the boolean: null formats to '--', and a missing
    // value would skip the checkbox. Hide that text so the cell stays checkbox-only.
    hideValue: true,
  };
}

function applyCellRendererDecorators(
  tableColumnConfigDto: TableColumnConfigDto,
  columnsConfig: TableColumnConfigDto[],
  params: ICellRendererParams,
  cellRendererConfig?: CellRendererSelectorResult,
  options?: CellRendererSelectorOptions
) {
  if (
    isPageFilterColumnConfig(tableColumnConfigDto) &&
    options?.isFullScreenMode !== true
  ) {
    return {
      component: memo(CheckboxCellRendererDecorator),
      params: getCheckboxCellRendererDecoratorParams(
        params,
        cellRendererConfig,
        tableColumnConfigDto,
        options
      ),
    };
  }
  if (
    tableColumnConfigDto.type === 'checkbox' &&
    tableColumnConfigDto.enableInlineEditing === true &&
    !params.node.group &&
    options?.isGroupOnlyMode !== true
  ) {
    return {
      component: memo(CheckboxCellRendererDecorator),
      params: getInlineEditableCellRendererParams(
        params,
        tableColumnConfigDto,
        options
      ),
    };
  }
  if (isTableColumnConfigWithDetailPane(tableColumnConfigDto)) {
    const detailPaneConfigId =
      params.data?.originalRowIndex != null &&
      options?.dataConfigs?.[params.data.originalRowIndex]?.detailPaneId;

    if (detailPaneConfigId) {
      return getDetailPaneCellRendererDecorator(
        detailPaneConfigId,
        params,
        tableColumnConfigDto,
        columnsConfig,
        cellRendererConfig
      );
    }
  }
  return cellRendererConfig;
}

export function getCellRendererSelector(
  tableColumnConfigDto: TableColumnConfigDto,
  widgetId: WidgetDto['id'],
  isFooterLoading: boolean,
  columnsConfig: TableColumnConfigDto[],
  options?: CellRendererSelectorOptions
): CellRendererSelectorFunc {
  return (params) => {
    // do not apply custom cell renderers for auto columns like grouping columns etc.
    if (params.column?.getColId() === GROUP_AUTO_COLUMN_ID) {
      return undefined;
    }

    if (params.node.isRowPinned()) {
      return {
        component: FooterCellRenderer,
        params: getFooterParams(
          tableColumnConfigDto,
          params,
          isFooterLoading,
          options?.footerTitleColumnKey
        ),
      };
    }
    const cellRendererConfig = getDefaultCellRenderer(
      tableColumnConfigDto,
      params,
      widgetId,
      options
    );

    return applyCellRendererDecorators(
      tableColumnConfigDto,
      columnsConfig,
      params,
      cellRendererConfig,
      options
    );
  };
}
